import { useEffect, useState } from "react";
import { api, formatUSD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Hammer, Armchair, ChevronRight, MonitorPlay, ExternalLink, Edit3, Save, XCircle, Plus, Minus, Trash2 } from "lucide-react";

const TYPE_META = {
  proyecto_especial: { label: "PROYECTO ESPECIAL", Icon: Hammer, cls: "bg-violet-500/10 text-violet-300 border border-violet-500/40" },
  alquiler_tecnica: { label: "ALQUILER TÉCNICA Y MOBILIARIO", Icon: Armchair, cls: "bg-teal-500/10 text-teal-300 border border-teal-500/40" },
};

const STATUS_META = {
  enviado_para_revisar: { label: "ENVIADO PARA REVISAR", cls: "bg-amber-500/10 text-amber-300 border border-amber-500/40" },
  aprobado: { label: "APROBADO", cls: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40" },
  rechazado: { label: "RECHAZADO", cls: "bg-red-500/10 text-red-300 border border-red-500/40" },
};

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type];
  if (!m) return null;
  return <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-semibold tracking-wider ${m.cls}`}><m.Icon className="w-3 h-3" /> {m.label}</span>;
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "bg-zinc-800 text-zinc-400 border border-zinc-700" };
  return <span className={`inline-block px-2 py-1 rounded-sm text-[10px] font-semibold tracking-wider ${m.cls}`}>{m.label}</span>;
};

export const ServiceRequestsPanel = ({ requests, reload }) => {
  const [filter, setFilter] = useState("todos");
  const [selected, setSelected] = useState(null);
  const [editStart, setEditStart] = useState(false);
  const filtered = requests.filter((r) => filter === "todos" || r.type === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="servicios-filter">
        {[["todos", "Todos"], ["proyecto_especial", "Proyecto Especial"], ["alquiler_tecnica", "Alquiler"]].map(([v, l]) => (
          <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
            className={`px-3 h-8 rounded-sm text-xs border transition ${filter === v ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"}`}>
            {l} · {requests.filter((r) => v === "todos" || r.type === v).length}
          </button>
        ))}
      </div>

      <Card className="bg-zinc-950 border-zinc-800 rounded-sm p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Tipo</th><th className="text-left p-3">Stand</th><th className="text-left p-3">Empresa</th><th className="text-left p-3">Contacto</th><th className="text-left p-3">Fecha</th><th className="text-left p-3">Estado</th><th className="text-left p-3">Total</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-zinc-500 text-xs">No hay solicitudes.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`service-row-${r.id}`} className="border-t border-zinc-800 hover:bg-zinc-900/50 transition">
                  <td className="p-3"><TypeBadge type={r.type} /></td>
                  <td className="p-3 font-mono text-white">{r.stand_number}</td>
                  <td className="p-3">{r.empresa}</td>
                  <td className="p-3 text-xs text-zinc-400">{r.nombre}<br /><span className="text-zinc-500">{r.email}</span></td>
                  <td className="p-3 text-xs text-zinc-400 font-mono">{r.created_at?.slice(0, 10)}</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3 font-mono text-[#FF5A00]">{r.type === "alquiler_tecnica" ? formatUSD(r.total || 0) : "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        data-testid={`modificar-service-${r.id}`}
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setSelected(r); setEditStart(true); }}
                        className="border-[#FF5A00]/40 text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-sm h-8 text-[10px] font-semibold tracking-wider uppercase px-2"
                        title="Modificar la solicitud en representación del expositor"
                      >
                        <Edit3 className="w-3 h-3 mr-1" /> Modificar
                      </Button>
                      <Button
                        data-testid={`open-service-${r.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelected(r); setEditStart(false); }}
                        className="hover:bg-zinc-800 rounded-sm h-8"
                        title="Ver detalle"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ServiceRequestDialog request={selected} openInEdit={editStart} onClose={() => { setSelected(null); setEditStart(false); }} reload={reload} />
    </div>
  );
};

function ServiceRequestDialog({ request, openInEdit, onClose, reload }) {
  const [newStatus, setNewStatus] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  const [products, setProducts] = useState([]);
  const [editMotivo, setEditMotivo] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [addProdId, setAddProdId] = useState("");
  const r = request;

  useEffect(() => {
    if (!r) return;
    setForm({
      empresa: r.empresa || "",
      nombre: r.nombre || "",
      email: r.email || "",
      whatsapp: r.whatsapp || "",
      descripcion: r.descripcion || "",
      observaciones: r.observaciones || "",
      items: (r.items || []).map((it) => ({ ...it })),
      led: r.led ? { ...r.led } : { ancho: "", alto: "", ubicacion: "", contenido: "", observaciones: "" },
    });
    setEditMode(!!openInEdit);
    setEditMotivo("");
    setAddProdId("");
    if (r.type === "alquiler_tecnica" && products.length === 0) {
      api.get("/servicios/productos").then(({ data }) => setProducts(Array.isArray(data) ? data : (data.rental_products || []))).catch(() => {});
    }
  }, [r?.id, openInEdit, r?.type, products.length]);

  const save = async () => {
    if (!newStatus) return toast.error("Elegí un estado");
    if (newStatus === "rechazado" && !motivo.trim()) return toast.error("El motivo del rechazo es obligatorio");
    setSaving(true);
    try {
      await api.post(`/admin/service-requests/${r.id}/status`, { status: newStatus, motivo: newStatus === "rechazado" ? motivo.trim() : null });
      toast.success("Estado actualizado y notificación enviada");
      reload();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al actualizar");
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editMotivo.trim()) return toast.error("El motivo de la modificación es obligatorio");
    setSavingEdit(true);
    try {
      const payload = {
        empresa: form.empresa, nombre: form.nombre, email: form.email, whatsapp: form.whatsapp,
        observaciones: form.observaciones, motivo: editMotivo.trim(),
      };
      if (r.type === "proyecto_especial") payload.descripcion = form.descripcion;
      if (r.type === "alquiler_tecnica") {
        payload.items = (form.items || []).filter((it) => it.quantity > 0).map((it) => ({ product_id: it.product_id, quantity: it.quantity }));
        payload.led = form.led;
      }
      const { data } = await api.put(`/admin/service-requests/${r.id}`, payload);
      if (data && data.changed === false) {
        toast.info(data.message || "No hubo cambios para guardar");
      } else {
        toast.success("Modificación guardada y notificación enviada al expositor.");
      }
      reload();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al modificar");
    } finally { setSavingEdit(false); }
  };

  const changeQty = (idx, delta) => {
    setForm((f) => {
      const items = [...(f.items || [])];
      items[idx] = { ...items[idx], quantity: Math.max(0, (items[idx].quantity || 0) + delta) };
      return { ...f, items };
    });
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const addItem = () => {
    if (!addProdId) return toast.error("Elegí un producto del catálogo");
    const p = products.find((x) => x.id === addProdId);
    if (!p) return;
    if ((form.items || []).some((it) => it.product_id === p.id)) return toast.error("Ese producto ya está en el pedido — subí la cantidad.");
    setForm((f) => ({ ...f, items: [...(f.items || []), { product_id: p.id, name: p.name, medidas: p.medidas || "", quantity: 1, unit_price: p.price, subtotal: p.price || 0, a_cotizar: !!p.a_cotizar || p.price == null }] }));
    setAddProdId("");
  };
  const previewTotal = (form?.items || []).reduce((s, it) => s + (it.a_cotizar ? 0 : (it.unit_price || 0) * (it.quantity || 0)), 0);

  if (!r || !form) return null;
  const led = form.led || {};
  const hasLed = Object.values(led).some((v) => v);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { setNewStatus(""); setMotivo(""); onClose(); } }}>
      <DialogContent data-testid="service-detail-dialog" className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 text-base">
            <TypeBadge type={r.type} /> Stand {r.stand_number} · {r.empresa}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={r.status} />
            <span className="text-xs text-zinc-500 font-mono">{r.created_at?.slice(0, 16).replace("T", " ")} UTC · {r.id}</span>
            <div className="flex-1" />
            {!editMode ? (
              <Button data-testid="service-edit-open-btn" size="sm" onClick={() => setEditMode(true)}
                className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-8 text-xs">
                <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Modificar
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setEditMode(false)} className="text-zinc-400 hover:text-white h-8 text-xs">
                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancelar edición
              </Button>
            )}
          </div>

          {r.status === "rechazado" && r.rechazo_motivo && (
            <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-3 text-xs text-zinc-200"><b>Motivo del rechazo:</b> {r.rechazo_motivo}</div>
          )}

          {editMode && r.status !== "enviado_para_revisar" && (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-xs text-amber-100" data-testid="edit-warning">
              ⚠ Esta solicitud está en estado <b>{STATUS_META[r.status]?.label || r.status}</b>. Los cambios quedan registrados en el historial y se envían por correo al expositor.
            </div>
          )}

          {/* Datos generales */}
          <div className={`grid grid-cols-2 gap-2 text-xs border rounded-sm p-3 ${editMode ? "border-[#FF5A00]/40 bg-[#FF5A00]/5" : "border-zinc-800 bg-zinc-900"}`}>
            {["empresa", "nombre", "email", "whatsapp"].map((k) => (
              <div key={k}>
                <div className="text-zinc-500 text-[10px] uppercase mb-1">{k}</div>
                {editMode ? (
                  <Input data-testid={`edit-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                ) : (
                  <div className="text-zinc-200">{r[k] || "—"}</div>
                )}
              </div>
            ))}
          </div>

          {r.type === "proyecto_especial" && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Descripción del proyecto</div>
              {editMode ? (
                <Textarea data-testid="edit-descripcion" rows={4} className="bg-zinc-950 border-[#FF5A00]/40 rounded-sm text-xs" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              ) : (
                <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-900 text-zinc-200 whitespace-pre-wrap text-xs">{r.descripcion || "—"}</div>
              )}
            </div>
          )}

          {r.type === "alquiler_tecnica" && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Productos</div>
              <div className="border border-zinc-800 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-900 text-zinc-500"><tr><th className="text-left p-2">Producto</th><th className="text-right p-2">Cant.</th><th className="text-right p-2">P.U.</th><th className="text-right p-2">Subtotal</th>{editMode && <th className="p-2"></th>}</tr></thead>
                  <tbody>
                    {(form.items || []).map((it, i) => (
                      <tr key={it.product_id || `it-${i}`} className="border-t border-zinc-800">
                        <td className="p-2">{it.name}{it.medidas ? <span className="text-zinc-500 font-mono"> · {it.medidas}</span> : null}</td>
                        <td className="p-2 text-right font-mono">
                          {editMode ? (
                            <div className="flex items-center gap-1 justify-end">
                              <button type="button" data-testid={`item-minus-${i}`} onClick={() => changeQty(i, -1)} className="w-6 h-6 border border-zinc-700 hover:bg-zinc-800 rounded-sm inline-flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                              <span className="min-w-6 text-center">{it.quantity}</span>
                              <button type="button" data-testid={`item-plus-${i}`} onClick={() => changeQty(i, 1)} className="w-6 h-6 border border-zinc-700 hover:bg-zinc-800 rounded-sm inline-flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                            </div>
                          ) : it.quantity}
                        </td>
                        <td className="p-2 text-right font-mono">{it.a_cotizar ? <span className="text-amber-300 uppercase text-[10px]">A cotizar</span> : formatUSD(it.unit_price)}</td>
                        <td className="p-2 text-right font-mono">{it.a_cotizar ? <span className="text-amber-300 uppercase text-[10px]">A cotizar</span> : formatUSD((it.unit_price || 0) * (it.quantity || 0))}</td>
                        {editMode && (
                          <td className="p-2 text-right">
                            <button type="button" data-testid={`item-remove-${i}`} onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="border-t border-zinc-700 bg-zinc-900 font-semibold">
                      <td className="p-2" colSpan={editMode ? 3 : 3}>TOTAL PRODUCTOS</td>
                      <td className="p-2 text-right font-mono text-[#FF5A00]">{formatUSD(editMode ? previewTotal : (r.total || 0))} + IVA</td>
                      {editMode && <td />}
                    </tr>
                  </tbody>
                </table>
              </div>
              {editMode && products.length > 0 && (
                <div className="mt-2 flex gap-2 items-center">
                  <Select value={addProdId} onValueChange={setAddProdId}>
                    <SelectTrigger data-testid="edit-add-product" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 text-xs flex-1"><SelectValue placeholder="Agregar producto del catálogo..." /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-white max-h-[280px]">
                      {products.filter((p) => !p.hidden).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} · {p.medidas || ""} · {p.a_cotizar || p.price == null ? "A cotizar" : formatUSD(p.price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button data-testid="edit-add-product-btn" type="button" size="sm" onClick={addItem} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm h-9 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</Button>
                </div>
              )}
            </div>
          )}

          {r.type === "alquiler_tecnica" && (hasLed || editMode) && (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-xs">
              <div className="font-semibold mb-1.5 flex items-center gap-2 text-amber-300"><MonitorPlay className="w-3.5 h-3.5" /> Pantalla LED (a cotizar)</div>
              <div className="grid grid-cols-2 gap-2 text-zinc-300">
                {["ancho", "alto", "ubicacion", "contenido"].map((k) => (
                  <div key={k}>
                    <div className="text-zinc-500 text-[10px] uppercase mb-0.5">{k}</div>
                    {editMode ? (
                      <Input data-testid={`edit-led-${k}`} value={form.led[k] || ""} onChange={(e) => setForm({ ...form, led: { ...form.led, [k]: e.target.value } })} className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                    ) : (
                      <div>{led[k] || "—"}</div>
                    )}
                  </div>
                ))}
              </div>
              {editMode ? (
                <Textarea data-testid="edit-led-obs" rows={2} className="bg-zinc-950 border-zinc-800 rounded-sm mt-2 text-xs" value={form.led.observaciones || ""} onChange={(e) => setForm({ ...form, led: { ...form.led, observaciones: e.target.value } })} placeholder="Observaciones LED" />
              ) : (led.observaciones && <div className="mt-1 text-zinc-300"><span className="text-zinc-500">Obs.:</span> {led.observaciones}</div>)}
            </div>
          )}

          {(r.archivos || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Archivos adjuntos</div>
              {r.archivos.map((a, i) => (
                <a key={a || `arch-${i}`} href={a} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-sky-300 hover:underline py-0.5"><ExternalLink className="w-3 h-3" /> {a}</a>
              ))}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Observaciones</div>
            {editMode ? (
              <Textarea data-testid="edit-observaciones" rows={2} className="bg-zinc-950 border-zinc-800 rounded-sm text-xs" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            ) : (
              <div className="text-xs text-zinc-300">{r.observaciones || "—"}</div>
            )}
          </div>

          {editMode ? (
            <div className="border border-[#FF5A00]/50 bg-[#FF5A00]/5 rounded-sm p-3 space-y-3" data-testid="edit-actions">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Motivo de la modificación (obligatorio)</div>
                <Textarea data-testid="edit-motivo" rows={2} className="bg-zinc-950 border-zinc-800 rounded-sm text-xs" placeholder="Ej: El expositor pidió sumar 2 sillas y corregir el mail." value={editMotivo} onChange={(e) => setEditMotivo(e.target.value)} />
              </div>
              <Button data-testid="edit-save-btn" onClick={saveEdit} disabled={savingEdit} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-10 text-xs">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar cambios y notificar
              </Button>
              <div className="text-[10px] text-zinc-500">Se registrará la modificación en el historial y se enviará por correo al expositor, con copia a info@ y celeste@, indicando que fue realizada por Servicios Oficiales.</div>
            </div>
          ) : (
            <div className="border-t border-zinc-800 pt-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Cambiar estado</div>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="service-status-select" className="bg-zinc-900 border-zinc-800 rounded-sm"><SelectValue placeholder="Elegir estado..." /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                  <SelectItem value="enviado_para_revisar">ENVIADO PARA REVISAR</SelectItem>
                  <SelectItem value="aprobado">APROBADO</SelectItem>
                  <SelectItem value="rechazado">RECHAZADO</SelectItem>
                </SelectContent>
              </Select>
              {newStatus === "rechazado" && (
                <Textarea data-testid="service-motivo" rows={2} className="bg-zinc-900 border-zinc-800 rounded-sm mt-2" placeholder="Motivo del rechazo (obligatorio, se envía al expositor)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              )}
              <Button data-testid="service-status-save-btn" onClick={save} disabled={saving} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm mt-3">Guardar estado</Button>
              <div className="text-[10px] text-zinc-500 mt-2">Al aprobar o rechazar se envía un correo automático al expositor.</div>
            </div>
          )}

          {(r.history || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Historial</div>
              {r.history.slice().reverse().map((h, i) => (
                <div key={h.date ? `${h.date}-${h.action}` : `hist-${i}`} className="text-[11px] text-zinc-400 border-l-2 border-zinc-800 pl-2 py-0.5">
                  <span className="font-mono text-zinc-600 mr-2">{h.date?.slice(0, 16).replace("T", " ")}</span>{h.user} · {h.comment}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
